import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  View,
  Text,
  Flex,
  ComboBox,
  Item,
  Button,
} from "@adobe/react-spectrum";
import { RootState } from "../../../store/storeConfig";
import { ConnectTransferEndppoint } from "../../../types/call.type";
import Emitter from "../../../emitter";
import { API_CALL_ADD_QUEUE_CONNECTION } from "../../../constants/constants";
import storeRTK from "../../../store/storeRTK";
import { setSelectedTab } from "../../../actions/common";
import CallDialPad from "../CallDialer/CallDialpad";

interface ConferenceDialogProps {
  onClose?: () => void;
}

const ConferenceDialog = ({ onClose }: ConferenceDialogProps) => {
  const call = useSelector((state: RootState) => state.call);
  const [comboBoxValue, setComboBoxValue] = useState("");
  const [selectedEndpoint, setSelectedEndpoint] =
    useState<ConnectTransferEndppoint | null>(null);

  // Get allowed transfer endpoints from call state
  const allowedTransferEndpoints = useMemo(() => {
    return (
      call?.allowedTransferEndpoints?.filter(
        (endpoint: ConnectTransferEndppoint) =>
          endpoint.id && endpoint.name && endpoint.quick_connect_type
      ) || []
    );
  }, [call?.allowedTransferEndpoints]);

  const handleComboBoxSelectionChange = (key: any) => {
    if (key) {
      const endpoint = allowedTransferEndpoints.find(
        (endpoint: ConnectTransferEndppoint) => endpoint.id === key
      );
      if (endpoint) {
        setSelectedEndpoint(endpoint);
        setComboBoxValue(endpoint.name);
      }
    }
  };

  const handleComboBoxInputChange = (value: string) => {
    setComboBoxValue(value);
    // Reset selected endpoint when typing
    if (selectedEndpoint) {
      setSelectedEndpoint(null);
    }

    return "";
  };

  const dialFromComboBox = () => {
    if (selectedEndpoint) {
      // Handle queue connection to conference - add queue to existing call
      Emitter.emit(API_CALL_ADD_QUEUE_CONNECTION, {
        queueName: selectedEndpoint.name,
        transferEndpoint: selectedEndpoint,
      });
      storeRTK.dispatch(setSelectedTab("Call"));
    }
    setComboBoxValue("");
    onClose?.();
  };

  return (
    <View>
      <View>
        <View>
          <Flex gap="size-100" alignItems="end">
            <ComboBox
              UNSAFE_className="call-inProgress-combobox"
              width={"100%"}
              inputValue={comboBoxValue}
              onInputChange={handleComboBoxInputChange}
              onSelectionChange={handleComboBoxSelectionChange}
              label="Select Quick Connects"
            >
              {allowedTransferEndpoints.map(
                (endpoint: ConnectTransferEndppoint) => (
                  <Item key={endpoint.id}>{endpoint.name}</Item>
                )
              )}
            </ComboBox>
            {selectedEndpoint && (
              <Button variant="cta" onPress={dialFromComboBox}>
                Call
              </Button>
            )}
          </Flex>
        </View>
        <Flex width={"100%"} alignItems={"center"}>
          <View
            borderBottomWidth="thin"
            borderBottomColor="gray-200"
            width={"100%"}
          ></View>
          <View UNSAFE_className="dialer-text" marginY={32}>Or dial manually</View>
          <View
            borderBottomWidth="thin"
            borderBottomColor="gray-200"
            width={"100%"}
          ></View>
        </Flex>
      </View>
      <Flex>
        <CallDialPad callType="conference" onClose={onClose} />
      </Flex>
    </View>
  );
};

export default ConferenceDialog;
